/* eslint-disable no-nested-ternary */
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import styled, { css } from 'styled-components';

import 'react-datepicker/dist/react-datepicker.css';

import { Icon } from '@powdr/components/icon';
import {
  Breakpoints, ColorProfiles, FontFamily, FontSize, ThemeProps as Prop,
} from '@powdr/constants';
import {
  getFontStyles, getMaxSelectableDate, getMinSelectableDate, themeUtil,
} from '@powdr/utils';

const DatePickerWrapper = styled.div`
  display: flex;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  height: 52px;
  width: 100%;
  ${() => getFontStyles(FontFamily.BASE_FONT, FontSize.REG50, undefined, undefined)};
  border: 1px solid rgba(0, 0, 0, .15);
  position: relative;

  ${Breakpoints.desktopOnly(css`
    max-width: 275px;
  `)}

  ${Breakpoints.mobile(css`
    margin-bottom: 15px;
  `)}

  .react-datepicker-popper {
    z-index: 1000;
  }

  .react-datepicker-wrapper, .react-datepicker__input-container {
    width: 100%;
    height: 100%;
  }

  .react-datepicker__input-container {
    display: flex;
  }

  .react-datepicker {
    font-family: inherit;
    font-size: inherit;

    // Fix for UX issue on datepicker via https://github.com/Hacker0x01/react-datepicker/issues/2930#issuecomment-1104174745
    // TODO: can possibly remove after next package update, check periodically for package update
    &__day--keyboard-selected,
    &__month-text--keyboard-selected,
    &__quarter-text--keyboard-selected,
    &__year-text--keyboard-selected {
      border-radius: inherit;
      background-color: inherit;
      color: inherit;
    }

    &__month--selected,
    &__month--in-selecting-range,
    &__month--in-range,
    &__quarter--selected,
    &__quarter--in-selecting-range,
    &__quarter--in-range {
      border-radius: 0.3rem;
      background-color: #216ba5;
      color: #fff;
    }

    &__header {
      background-color: inherit;
      border-bottom: none;
    }

    &__day--today {
      background-color: #e4eef5;
      color: #404040;
      border-radius: 4px;
      font-weight: inherit;
    }
  }

  input {
    width: 100%;
    border: none;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }
`;

const DatePickerIcon = styled(Icon)`
  display: flex;
  position: absolute;
  right: 16px;
  pointer-events: none;
  align-items: center;
  margin-left: 5px;
  
  svg {
    fill: ${({ $useColorProfile, $colorProfile }) => themeUtil(Prop.ICON, undefined, $useColorProfile ? $colorProfile : null)};
    color: ${({ $useColorProfile, $colorProfile }) => themeUtil(Prop.ICON, undefined, $useColorProfile ? $colorProfile : null)};
  }
`;

export const DatePicker = ({
  className,
  id,
  ariaLabel,
  ariaLabelledBy,
  colorProfile = ColorProfiles.BASE,
  useColorProfile = false,
  isCurrentDaySelectable = false,
  placeholder = 'Select Date...',
  prettyDate = 'MMM dd yyyy',
  dateFormat = 'MM/dd/yyyy',
  isDefaultDate = false,
  isAutoSelected = false,
  handleDateChange = () => {},
  minDate = null,
  minBookableDate = null,
  maxNumDays = 365,
  maxBookableDate = null,
}) => {
  const [date, setDate] = useState(null);
  const minSelectableDate = () => getMinSelectableDate({
    minBookableDate,
    minDate,
    isCurrentDaySelectable,
  });

  const maxSelectableDate = getMaxSelectableDate({ maxBookableDate, maxNumDays });

  const minDateValue = minSelectableDate();
  const selectedDate = date || ((isDefaultDate || isAutoSelected) ? minDateValue : null);

  const onChangeDate = (d) => {
    setDate(d);
    handleDateChange(d);
  };

  return (
    <DatePickerWrapper className={className}>
      <ReactDatePicker
        id={id}
        title={ariaLabel}
        ariaLabel={ariaLabel}
        ariaLabelledBy={ariaLabelledBy}
        minDate={minDateValue}
        maxDate={maxSelectableDate}
        dateFormat={dateFormat}
        selected={selectedDate}
        onChange={onChangeDate}
        value={date
          ? format(date, prettyDate)
          : (isDefaultDate || isAutoSelected)
            ? format(minDateValue, prettyDate)
            : placeholder}
        /* customInput={(
          <DatePickerCustomInput colorProfile={colorProfile}>
            <span>
              {date
                ? format(date, prettyDate)
                : (isDefaultDate || isAutoSelected)
                  ? format(minSelectableDate(), prettyDate)
                  : placeholder}
            </span>
            <DatePickerIcon
              colorProfile={colorProfile}
              name="ui-calendar-picker"
              width="20"
              height="20"
            />
          </DatePickerCustomInput>
        )} */
      />
      <DatePickerIcon
        $useColorProfile={useColorProfile}
        $colorProfile={colorProfile}
        name="ui-calendar-picker"
        width="20"
        height="20"
      />
    </DatePickerWrapper>
  );
};

DatePicker.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  colorProfile: PropTypes.string,
  useColorProfile: PropTypes.bool,
  isCurrentDaySelectable: PropTypes.bool,
  placeholder: PropTypes.string,
  prettyDate: PropTypes.string,
  dateFormat: PropTypes.string,
  isDefaultDate: PropTypes.bool,
  isAutoSelected: PropTypes.bool,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minBookableDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxNumDays: PropTypes.number,
  maxBookableDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  handleDateChange: PropTypes.func,
};
